import React, { useState } from 'react'
import PropTypes from 'prop-types'
import Box from '@material-ui/core/Box'
import Button from '@material-ui/core/Button'
import IconButton from '@material-ui/core/IconButton'
import FormHelperText from '@material-ui/core/FormHelperText'
import Typography from '@material-ui/core/Typography'
import AddAPhotoIcon from 'components/material-symbol/icons/AddAPhoto'
import CloseIcon from 'components/material-symbol/icons/Close'
import DescriptionIcon from 'components/material-symbol/icons/Description'
import useIconAccessor from 'components/lesson-type-category/icons'
import { getVisibleOfferings } from 'pages/pga-coach/book-student/coach-book-student-lesson-utils'
import { useStyles as useFlowStyles } from 'pages/pga-coach/bookings/events/edit/edit-event-form/styles'
import { uploadMedia } from 'lib/cloudinary'
import { CLOUDINARY_CLOUDNAME, CLOUDINARY_UPLOAD_PRESET } from 'env'
import { useSnackbar, useSnackbarError } from 'lib/snackbar'
import { MAX_MEDIA, MAX_OFFERINGS, MESSAGE_MAX_LENGTH } from '../constants'
import { formatShortDate } from '../format'
import useStyles from '../styles'
import FormSection from './form-section'
import MessageEditor from './message-editor'
import OfferingCard from './offering-card'

const UPLOAD_OPTIONS = {
  cloudName: CLOUDINARY_CLOUDNAME,
  upload_preset: CLOUDINARY_UPLOAD_PRESET,
  sources: ['local'],
  multiple: false,
  folder: 'coach-campaigns',
  showPoweredBy: false,
}

const IMAGE_OPTIONS = {
  ...UPLOAD_OPTIONS,
  resourceType: 'image',
  clientAllowedFormats: ['png', 'jpeg', 'jpg', 'gif', 'webp'],
  maxFileSize: 10000000,
}

const FILE_OPTIONS = {
  ...UPLOAD_OPTIONS,
  resourceType: 'auto',
  clientAllowedFormats: ['pdf'],
  maxFileSize: 20000000,
}

const fileTitle = info =>
  [info.original_filename, info.format].filter(Boolean).join('.')

const offeringShape = PropTypes.shape({
  id: PropTypes.string.isRequired,
  title: PropTypes.string.isRequired,
  startDateTime: PropTypes.string,
})

const OfferingGroup = ({
  title,
  offeringType,
  options,
  selected,
  onToggle,
}) => {
  const classes = useStyles()
  const iconAccessor = useIconAccessor()
  const [expanded, setExpanded] = useState(false)
  const { icon } =
    offeringType === 'GROUP_EVENT' ? iconAccessor.GROUP : iconAccessor.PRIVATE
  const unavailable = selected
    .filter(
      offering =>
        offering.offeringType === offeringType &&
        !options.some(option => option.id === offering.id),
    )
    .map(offering => ({ id: offering.id, title: offering.title }))
  const rows = [...options, ...unavailable]
  if (rows.length === 0) return null

  const atLimit = selected.length >= MAX_OFFERINGS
  const isChecked = option =>
    selected.some(
      offering =>
        offering.offeringType === offeringType && offering.id === option.id,
    )
  const { visible, hiddenCount } = getVisibleOfferings(rows, {
    expanded,
    isSelected: isChecked,
  })

  return (
    <FormSection title={title}>
      <Box className={classes.listStack}>
        <Box className={classes.cardStack}>
          {visible.map(option => {
            const checked = isChecked(option)
            const secondary = unavailable.includes(option)
              ? 'No longer available'
              : option.startDateTime
                ? `Group event · ${formatShortDate(option.startDateTime)}`
                : 'Booking type'
            return (
              <OfferingCard
                key={option.id}
                title={option.title}
                description={secondary}
                icon={icon}
                selected={checked}
                disabled={!checked && atLimit}
                onClick={() => onToggle(offeringType, option)}
                data-testid={`campaign-offering-${offeringType}-${option.id}`}
              />
            )
          })}
        </Box>
        {hiddenCount > 0 ? (
          <Button
            color="primary"
            size="small"
            style={{ alignSelf: 'flex-start' }}
            onClick={() => setExpanded(true)}
          >
            View {hiddenCount} more
          </Button>
        ) : null}
      </Box>
    </FormSection>
  )
}

OfferingGroup.propTypes = {
  title: PropTypes.string.isRequired,
  offeringType: PropTypes.oneOf(['LESSON_TYPE', 'GROUP_EVENT']).isRequired,
  options: PropTypes.arrayOf(offeringShape).isRequired,
  selected: PropTypes.array.isRequired,
  onToggle: PropTypes.func.isRequired,
}

const MediaTile = ({ item, onRemove }) => {
  const classes = useStyles()

  return (
    <Box className={classes.mediaTile}>
      {item.kind === 'IMAGE' ? (
        <img
          src={item.url}
          alt={item.title || ''}
          className={classes.mediaImage}
        />
      ) : (
        <>
          <DescriptionIcon />
          <Typography className={classes.mediaTitle}>
            {item.title || 'Attachment'}
          </Typography>
        </>
      )}
      <IconButton
        size="small"
        className={classes.mediaRemove}
        onClick={onRemove}
        aria-label={`Remove ${item.title || 'attachment'}`}
      >
        <CloseIcon fontSize="small" />
      </IconButton>
    </Box>
  )
}

MediaTile.propTypes = {
  item: PropTypes.shape({
    kind: PropTypes.oneOf(['IMAGE', 'FILE']).isRequired,
    url: PropTypes.string.isRequired,
    title: PropTypes.string,
  }).isRequired,
  onRemove: PropTypes.func.isRequired,
}

export default function ContentStep({
  campaign,
  offeringOptions,
  bodyError,
  onChange,
}) {
  const classes = useStyles()
  const flowClasses = useFlowStyles()
  const { enqueueSnackbar } = useSnackbar()
  const showError = useSnackbarError(enqueueSnackbar)
  const { body, offerings, media } = campaign
  const mediaRemaining = MAX_MEDIA - media.length

  const toggleOffering = (offeringType, option) => {
    const exists = offerings.some(
      offering =>
        offering.offeringType === offeringType && offering.id === option.id,
    )
    onChange({
      offerings: exists
        ? offerings.filter(
          offering =>
            !(
              offering.offeringType === offeringType &&
              offering.id === option.id
            ),
        )
        : [...offerings, { offeringType, id: option.id, title: option.title }],
    })
  }

  const addMedia = items =>
    onChange({ media: [...media, ...items].slice(0, MAX_MEDIA) })

  const upload = async kind => {
    try {
      const info = await uploadMedia(
        kind === 'IMAGE' ? IMAGE_OPTIONS : FILE_OPTIONS,
      )
      if (!info) return
      addMedia([
        kind === 'IMAGE'
          ? { kind, url: info.secure_url, title: fileTitle(info) }
          : {
            kind,
            url: info.secure_url,
            title: fileTitle(info),
            mimeType: 'application/pdf',
          },
      ])
    } catch (e) {
      showError('That upload didn’t work. Try again.')
    }
  }

  return (
    <>
      <FormSection
        title="Message"
        description={
          'Students get this in chat from you. Anyone who hasn’t opened the app gets it by email too.'
        }
      >
        <Box>
          <MessageEditor
            value={body}
            placeholder="Spring clinics are open! I saved spots for my students first."
            onChange={value => onChange({ body: value })}
          />
          <FormHelperText error={bodyError || body.length > MESSAGE_MAX_LENGTH}>
            {bodyError && !body.trim()
              ? 'Write a message'
              : body.length > MESSAGE_MAX_LENGTH
                ? `Keep it under ${MESSAGE_MAX_LENGTH} characters (${body.length} / ${MESSAGE_MAX_LENGTH})`
                : `${body.length} / ${MESSAGE_MAX_LENGTH}`}
          </FormHelperText>
        </Box>
      </FormSection>

      <FormSection
        title="Offerings"
        description={`Add up to ${MAX_OFFERINGS}. Each one shows as a card students can book from, and we track bookings that come from it.`}
      >
        <Box className={classes.sectionContent}>
          {offeringOptions.lessonTypes.length === 0 &&
          offeringOptions.groupEvents.length === 0 ? (
            <Typography className={classes.helperText}>
              You don’t have any active booking types or upcoming group events
              to add.
            </Typography>
          ) : null}
          <OfferingGroup
            title="Booking types"
            offeringType="LESSON_TYPE"
            options={offeringOptions.lessonTypes}
            selected={offerings}
            onToggle={toggleOffering}
          />
          <OfferingGroup
            title="Upcoming group events"
            offeringType="GROUP_EVENT"
            options={offeringOptions.groupEvents}
            selected={offerings}
            onToggle={toggleOffering}
          />
        </Box>
      </FormSection>

      <FormSection
        title="Photos and guides"
        description={`Add promo images or PDF practice guides. Up to ${MAX_MEDIA}.`}
      >
        <Box className={classes.sectionContent}>
          {media.length > 0 ? (
            <Box className={classes.mediaGrid}>
              {media.map((item, index) => (
                <MediaTile
                  key={`${item.kind}-${item.url}-${index}`}
                  item={item}
                  onRemove={() =>
                    onChange({ media: media.filter((_, i) => i !== index) })
                  }
                />
              ))}
            </Box>
          ) : null}
          <Box className={classes.mediaActions}>
            <Button
              variant="outlined"
              color="primary"
              className={flowClasses.outlinedButton}
              startIcon={<AddAPhotoIcon />}
              disabled={mediaRemaining <= 0}
              onClick={() => upload('IMAGE')}
            >
              Add Image
            </Button>
            <Button
              variant="outlined"
              color="primary"
              className={flowClasses.outlinedButton}
              startIcon={<DescriptionIcon />}
              disabled={mediaRemaining <= 0}
              onClick={() => upload('FILE')}
            >
              Add PDF
            </Button>
          </Box>
        </Box>
      </FormSection>
    </>
  )
}

ContentStep.propTypes = {
  campaign: PropTypes.shape({
    body: PropTypes.string.isRequired,
    offerings: PropTypes.array.isRequired,
    media: PropTypes.array.isRequired,
  }).isRequired,
  offeringOptions: PropTypes.shape({
    lessonTypes: PropTypes.arrayOf(offeringShape).isRequired,
    groupEvents: PropTypes.arrayOf(offeringShape).isRequired,
  }).isRequired,
  bodyError: PropTypes.bool,
  onChange: PropTypes.func.isRequired,
}
